import { memo } from 'react';

import type { SharedAvatarProps } from '../../types/SharedAvatarProps';

import { Layout } from '../Layout/Layout';
import { Skeleton } from '../Skeleton/Skeleton';
import { TextBlock } from '../TextBlock/TextBlock';
import { Tooltip } from '../Tooltip/Tooltip';
import { AvatarChip } from './subcomponents/AvatarChip';
import {
  getAvatarChipTooltip,
  getAvatarInlineText,
  getAvatarInlineTooltip,
} from './utils';
import './Avatar.css';

export type AvatarProps = SharedAvatarProps & {
  /** EDS-internal only — never set from src/**, only passed sibling-to-sibling within packages/eds. */
  __private?: {
    /** Forces chip-only layout inside `AvatarGroup` (no inline text). */
    inGroup?: boolean;
  };
};

export const Avatar = memo(
  ({
    color,
    isLoading = false,
    mode,
    name,
    secondaryText,
    secondaryTooltip,
    size = 'm',
    src,
    text,
    tooltip,
    truncate,
    __private,
  }: AvatarProps) => {
    const inGroup = __private?.inGroup === true;
    const resolvedName = name?.trim();
    const inlineText = getAvatarInlineText({
      inGroup,
      mode,
      name,
      text,
    });
    const inlineTooltip = inlineText
      ? getAvatarInlineTooltip({ mode, name, text, tooltip })
      : undefined;
    const chipTooltip = inlineText
      ? undefined
      : getAvatarChipTooltip({
          name,
          tooltip,
        });

    if (isLoading) {
      return (
        <Skeleton
          shape="avatar"
          label={resolvedName ? 'Loading user' : undefined}
        />
      );
    }

    const chip = <AvatarChip color={color} name={name} size={size} src={src} />;

    if (!inlineText) {
      const chipWithTooltip = chipTooltip ? (
        <Tooltip tooltip={chipTooltip} __private={{ wrapTrigger: true }}>
          {chip}
        </Tooltip>
      ) : (
        chip
      );

      return chipWithTooltip;
    }

    const inlineLabel = (
      <TextBlock
        secondaryText={mode === 'info' ? secondaryText : undefined}
        secondaryTooltip={mode === 'info' ? secondaryTooltip : undefined}
        text={inlineText}
        tooltip={inlineTooltip}
        truncate={truncate}
      />
    );

    return (
      <Layout
        align="center"
        className="eds-avatar--inline"
        direction="row"
        spacing="s"
      >
        <AvatarChip
          color={color}
          name={name}
          size={size}
          src={src}
          __private={{ decorative: true }}
        />
        {inlineLabel}
      </Layout>
    );
  },
);

Avatar.displayName = 'Avatar';
